import { Upload, Cpu, FileText, Rocket, Database, Globe, CheckCircle } from 'lucide-react';
import Reveal from '../components/animations/Reveal';

const HowItWorksPage = () => (
  <div className="py-16">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="text-center mb-16">
          <h2 className="font-display text-4xl font-bold text-slate-900 mb-6">How It <span className="text-brand">Works</span></h2>
          <p className="text-xl text-slate-600">Our comprehensive pipeline processes multiple input types for accurate Cry toxin identification</p>
        </div>
      </Reveal>

      {/* Workflow Diagram */}
      <Reveal delay={80}>
        <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm mb-12">
          <div className="text-center mb-6">
            <h3 className="font-display text-2xl font-bold text-slate-900 mb-2">CryMiner Pipeline Workflow</h3>
            <p className="text-slate-500">Complete analysis pipeline from input to results</p>
          </div>
          <div className="flex justify-center">
            <div className="w-full max-w-4xl bg-slate-50 rounded-2xl p-8 text-center border border-slate-200">
              <div className="text-lg font-semibold mb-4 text-slate-800">Pipeline Workflow Diagram</div>
              <div className="grid grid-cols-4 gap-4 text-sm items-center">
                {['Input Data', 'Processing', 'Analysis', 'Results'].map((label, i) => (
                  <div key={i} className="bg-brand-tint text-brand font-medium border border-blue-100 p-4 rounded-xl animate-stage-pulse" style={{ animationDelay: `${i * 0.5}s` }}>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 text-center">
            <div className="inline-flex items-center h-8 bg-slate-100 border border-slate-200 rounded-lg px-3 text-slate-600 text-sm">
              Powered by Oneomics Private Limited
            </div>
          </div>
        </div>
      </Reveal>

      {/* Pipeline Workflow */}
      <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm mb-12">
        <div className="grid lg:grid-cols-4 gap-8 relative">
          {[
            { Icon: Upload, title: 'Upload Genomic Data', text: 'Upload bacterial genome assemblies, raw sequencing data, or metagenomic datasets.' },
            { Icon: Cpu, title: 'Automated Analysis', text: 'Quality assessment, gene prediction, Cry toxin identification, functional annotation, and comparative analysis.' },
            { Icon: FileText, title: 'Comprehensive Reporting', text: 'Cry gene inventories, classification results, functional predictions, evolutionary insights, and candidate rankings.' },
            { Icon: Rocket, title: 'Actionable Insights', text: 'Support research publications, product development, patent applications, and regulatory submissions.' },
          ].map((step, i) => {
            const Icon = step.Icon;
            return (
              <Reveal key={i} delay={i * 150}>
                <div className="text-center relative group">
                  {i < 3 && (
                    <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-0.5 overflow-hidden">
                      <div className="w-full h-full bg-[length:16px_2px] animate-flow" style={{ backgroundImage: 'linear-gradient(90deg, rgba(10,103,242,0.6) 0 8px, transparent 8px 16px)' }}></div>
                    </div>
                  )}
                  <div className="relative bg-brand rounded-2xl w-16 h-16 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                    <span className="absolute inset-0 rounded-2xl bg-brand opacity-40 animate-ping-slow" style={{ animationDelay: `${i * 0.4}s` }}></span>
                    <Icon className="w-8 h-8 text-white relative z-10" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* Input Types */}
      <div className="grid md:grid-cols-3 gap-8">
        {[
          { title: 'INPUT A: Short Reads', subtitle: 'Illumina Sequencing', description: 'High-quality short reads processed through SPAdes assembly for accurate genome reconstruction.', icon: '📊', features: ['Quality control', 'Adapter trimming', 'SPAdes assembly', 'High throughput'] },
          { title: 'INPUT B: Assembled Genomes', subtitle: 'FASTA Format', description: 'Pre-assembled genome sequences directly analyzed without additional assembly steps.', icon: '🧬', features: ['Direct analysis', 'Rapid processing', 'FASTA input', 'No assembly needed'] },
          { title: 'INPUT C: Long Reads', subtitle: 'PacBio/Nanopore', description: 'Long reads assembled using Flye for improved contiguity and structural resolution.', icon: '🔬', features: ['Flye assembly', 'Error correction', 'High contiguity', 'Structural variants'] },
        ].map((it, index) => (
          <Reveal key={index} delay={index * 130}>
            <div className="h-full bg-white rounded-2xl p-6 border border-slate-200 hover:border-brand hover:-translate-y-2 hover:shadow-lg transition-all duration-300 group">
              <div className="text-3xl mb-4 inline-block group-hover:scale-125 group-hover:-rotate-6 transition-transform duration-300">{it.icon}</div>
              <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">{it.title}</h3>
              <div className="text-brand text-sm mb-3">{it.subtitle}</div>
              <p className="text-slate-600 mb-4">{it.description}</p>
              <div className="space-y-1">
                {it.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center text-sm text-slate-500 transition-all duration-300 group-hover:translate-x-1" style={{ transitionDelay: `${idx * 40}ms` }}>
                    <CheckCircle className="w-4 h-4 text-brand mr-2" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Technology Highlights */}
      <Reveal>
        <h3 className="font-display text-3xl font-bold text-slate-900 text-center mt-20 mb-10">Technology Highlights</h3>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-8">
        {[
          { icon: Cpu, title: 'Advanced Bioinformatics Engine', items: ['Genome mining algorithms', 'Hidden Markov Models (HMMs)', 'Machine-learning prediction', 'Structural bioinformatics'] },
          { icon: Database, title: 'Comprehensive Cry Database', items: ['Known Cry toxin families', 'Functional annotations', 'Structural information', 'Insect target associations'] },
          { icon: Globe, title: 'Scalable Cloud Infrastructure', items: ['High-performance computing', 'Secure data management', 'Rapid processing', 'Enterprise-grade reliability'] },
        ].map((tech, index) => {
          const Icon = tech.icon;
          return (
            <Reveal key={index} delay={index * 110}>
              <div className="h-full bg-white rounded-2xl p-6 border border-slate-200 hover:border-brand hover:-translate-y-2 hover:shadow-lg transition-all duration-300 group">
                <div className="inline-flex w-12 h-12 rounded-xl items-center justify-center bg-brand-tint mb-4">
                  <Icon className="w-7 h-7 text-brand group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h4 className="font-display text-lg font-semibold text-slate-900 mb-3">{tech.title}</h4>
                <div className="space-y-1.5">
                  {tech.items.map((it, i) => (
                    <div key={i} className="flex items-center text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-brand mr-2 flex-shrink-0" />
                      {it}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </div>
);

export default HowItWorksPage;
